import { useState, useEffect } from "react";

const C = {
  pink: "#F7A1C4", mint: "#9ED9C3", yellow: "#FFD966",
  blue: "#9CCBFF", lilac: "#D7B7F6", ink: "#1F1F1F", cream: "#FFF8F1",
  pinkSoft: "#FDE8F0", mintSoft: "#E6F5EE", yellowSoft: "#FFF7DC",
  blueSoft: "#E4F0FF", lilacSoft: "#F0E5FC",
};
const heading = "'Baloo 2', cursive, sans-serif";
const body = "'Nunito', sans-serif";

const IMG = { logo: "/images/logo.webp" };

const sections = [
  { id: "overview", label: "Overview", icon: "◉" },
  { id: "bookings", label: "Bookings", icon: "▦" },
  { id: "parties", label: "Party Enquiries", icon: "★" },
  { id: "schools", label: "School Enquiries", icon: "◆" },
  { id: "messages", label: "Messages", icon: "✉" },
  { id: "referrals", label: "Referrals", icon: "♥" },
  { id: "monday", label: "Monday Interest", icon: "◇" },
  { id: "classes", label: "Manage Classes", icon: "▣" },
];

const Stat = ({ label, value, color, sub }) => (
  <div style={{ background: "#fff", borderRadius: 16, border: `2px solid ${color}25`, padding: "20px", flex: "1 1 140px" }}>
    <div style={{ fontFamily: body, fontSize: 12, color: C.ink + "77", marginBottom: 4 }}>{label}</div>
    <div style={{ fontFamily: heading, fontSize: 28, fontWeight: 800, color: C.ink }}>{value}</div>
    {sub && <div style={{ fontFamily: body, fontSize: 11, color, fontWeight: 600, marginTop: 2 }}>{sub}</div>}
  </div>
);

// Parse a session date string like "Thu 19 Mar" or "Fri 10 Apr" into a Date
const parseSessionDate = (s) => {
  const months = {Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11};
  const clean = s.replace(/^(Mon|Tue|Wed|Thu|Fri|Sat|Sun) /, "").replace(/ (AM|PM)$/, "");
  const parts = clean.split(" ");
  const day = parseInt(parts[0]);
  const month = months[parts[1]];
  if (isNaN(day) || month === undefined) return null;
  return new Date(2026, month, day);
};

/* ═══ OVERVIEW ═══ */
const Overview = ({ data }) => {
  const bookings = data.bookings || [];
  const messages = data.messages || [];
  const parties = data.parties || [];

  const totalRevenue = bookings.reduce((s, b) => s + (parseFloat(b.Amount) || 0), 0);
  const paidBookings = bookings.filter(b => b.Status && b.Status.includes("Paid")).length;
  const newParties = parties.filter(e => e.Status === "New").length;
  const unreadMessages = messages.filter(m => m.Status === "Unread").length;

  // Count spots for upcoming sessions
  const MAX = 15;
  const sessionCounts = {};
  bookings.forEach(b => {
    const sessions = (b.Sessions || "").split(", ");
    const num = parseInt(b.NumChildren) || 1;
    sessions.forEach(s => { if (s) sessionCounts[s] = (sessionCounts[s] || 0) + num; });
  });

  // Find next upcoming session (any day)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const nextSession = Object.keys(sessionCounts)
    .filter(s => {
      const d = parseSessionDate(s);
      return d && d >= today;
    })
    .sort((a, b) => {
      const da = parseSessionDate(a);
      const db = parseSessionDate(b);
      return da - db;
    })[0];

  const nextCount = nextSession ? (sessionCounts[nextSession] || 0) : 0;
  const spotsLeft = MAX - nextCount;

  // Recent activity from all sources, sorted by date
  const activity = [
    ...bookings.slice(-5).reverse().map(b => ({
      text: `${b.Children} booked ${b.Type === "Term" ? b.Term : "drop-in"} (${b.NumSessions} session${b.NumSessions > 1 ? "s" : ""})`,
      time: b.Timestamp ? new Date(b.Timestamp).toLocaleDateString() : "",
      color: C.mint,
    })),
    ...parties.filter(p => p.Status === "New").map(p => ({
      text: `New party enquiry from ${p.Name} (${p.Package})`,
      time: p.Timestamp ? new Date(p.Timestamp).toLocaleDateString() : "",
      color: C.pink,
    })),
    ...messages.filter(m => m.Status === "Unread").map(m => ({
      text: `New message from ${m.Name}`,
      time: m.Timestamp ? new Date(m.Timestamp).toLocaleDateString() : "",
      color: C.lilac,
    })),
  ].slice(0, 8);

  return (
    <div>
      <h2 style={{ fontFamily: heading, fontSize: 22, fontWeight: 800, color: C.ink, marginBottom: 20 }}>Welcome back</h2>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 28 }}>
        <Stat label="Total Revenue" value={`£${totalRevenue.toFixed(2)}`} color={C.mint} sub={`${paidBookings} bookings`} />
        <Stat label={nextSession ? `Spots for ${nextSession}` : "Next Session"} value={`${nextCount}/${MAX}`} color={C.blue} sub={`${spotsLeft} spots left`} />
        <Stat label="New Enquiries" value={newParties} color={C.pink} sub={newParties > 0 ? "Needs attention" : "All clear"} />
        <Stat label="Unread Messages" value={unreadMessages} color={C.lilac} sub={unreadMessages > 0 ? "Needs attention" : "All clear"} />
      </div>
      <div style={{ fontFamily: heading, fontSize: 16, fontWeight: 700, color: C.ink, marginBottom: 12 }}>Recent activity</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {activity.length === 0 && <div style={{ fontFamily: body, fontSize: 13, color: C.ink + "77" }}>No recent activity</div>}
        {activity.map((a, i) => (
          <div key={i} style={{ background: "#fff", borderRadius: 12, padding: "12px 16px", border: `1.5px solid ${a.color}20`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: a.color }} />
              <span style={{ fontFamily: body, fontSize: 13, color: C.ink }}>{a.text}</span>
            </div>
            <span style={{ fontFamily: body, fontSize: 11, color: C.ink + "66" }}>{a.time}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ═══ BOOKINGS ═══ */
const Bookings = ({ data }) => {
  const bookings = (data.bookings || []).slice().reverse();
  const [expandedRow, setExpandedRow] = useState(null);
  // resendState keyed by "bookingIndex-sessionDate": 'sending' | 'sent' | 'error'
  const [resendState, setResendState] = useState({});

  const handleResend = async (b, bookingIdx, session) => {
    const key = `${bookingIdx}-${session}`;
    if (!b.Email) return;
    setResendState(s => ({ ...s, [key]: "sending" }));
    try {
      const res = await fetch("/.netlify/functions/resend-confirmation", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: b.Email,
          parentName: b.ParentName || "",
          childNames: b.Children || "",
          sessions: session,
          amount: b.Amount || "0",
          bookingType: b.Type || "drop-in",
          termName: b.Term || "",
        }),
      });
      const json = await res.json();
      setResendState(s => ({ ...s, [key]: json.success ? "sent" : "error" }));
    } catch {
      setResendState(s => ({ ...s, [key]: "error" }));
    }
  };

  return (
    <div>
      <h2 style={{ fontFamily: heading, fontSize: 22, fontWeight: 800, color: C.ink, marginBottom: 8 }}>Bookings ({bookings.length})</h2>
      <p style={{ fontFamily: body, fontSize: 13, color: C.ink + "66", marginBottom: 20 }}>Click a row to expand sessions and resend confirmation emails.</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {bookings.map((b, i) => {
          const isOpen = expandedRow === i;
          const sessions = (b.Sessions || "").split(", ").filter(Boolean);
          const hasEmail = !!b.Email;

          return (
            <div key={i} style={{
              borderRadius: 16,
              border: `1.5px solid ${isOpen ? C.pink + "55" : C.ink + "08"}`,
              background: "#fff",
              overflow: "hidden",
              transition: "border-color 0.2s",
            }}>
              {/* Summary row — clickable */}
              <div
                onClick={() => setExpandedRow(isOpen ? null : i)}
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 2fr 1.4fr 0.8fr 0.8fr 1fr 28px",
                  alignItems: "center",
                  gap: 8,
                  padding: "12px 16px",
                  cursor: "pointer",
                  background: isOpen ? C.pinkSoft : "transparent",
                  transition: "background 0.2s",
                }}
              >
                <div style={{ fontFamily: body, fontWeight: 700, fontSize: 13, color: C.ink }}>{b.Children}</div>
                <div style={{ fontFamily: body, fontSize: 12, color: C.ink + "88" }}>{b.Email || <span style={{ color: C.ink + "44", fontStyle: "italic" }}>No email</span>}</div>
                <div>
                  <span style={{
                    background: b.Type === "term" ? C.mintSoft : C.blueSoft,
                    color: b.Type === "term" ? C.mint : C.blue,
                    padding: "3px 10px", borderRadius: 10, fontSize: 11, fontWeight: 700,
                  }}>{b.Type}{b.Term ? ` (${b.Term})` : ""}</span>
                </div>
                <div style={{ fontFamily: body, fontSize: 12, color: C.ink + "88" }}>{b.NumSessions} session{b.NumSessions > 1 ? "s" : ""}</div>
                <div style={{ fontFamily: heading, fontWeight: 700, fontSize: 13, color: C.ink }}>£{parseFloat(b.Amount || 0).toFixed(2)}</div>
                <div>
                  <span style={{
                    background: b.Status && b.Status.includes("Paid") ? C.mintSoft : C.yellowSoft,
                    color: b.Status && b.Status.includes("Paid") ? C.mint : C.yellow,
                    padding: "3px 10px", borderRadius: 10, fontSize: 11, fontWeight: 700,
                  }}>{b.Status}</span>
                </div>
                <div style={{
                  fontFamily: body, fontSize: 14, color: C.ink + "44",
                  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                  textAlign: "center",
                }}>▾</div>
              </div>

              {/* Expanded detail panel */}
              {isOpen && (
                <div style={{ padding: "0 16px 16px", borderTop: `1px solid ${C.pink}22` }}>
                  <div style={{ paddingTop: 14 }}>
                    {/* Parent info strip */}
                    <div style={{ display: "flex", gap: 20, marginBottom: 14, flexWrap: "wrap" }}>
                      {b.ParentName && <span style={{ fontFamily: body, fontSize: 12, color: C.ink + "88" }}><span style={{ color: C.ink + "55" }}>Parent:</span> {b.ParentName}</span>}
                      {b.ParentPhone && <span style={{ fontFamily: body, fontSize: 12, color: C.ink + "88" }}><span style={{ color: C.ink + "55" }}>Phone:</span> {b.ParentPhone}</span>}
                      {b.Ages && <span style={{ fontFamily: body, fontSize: 12, color: C.ink + "88" }}><span style={{ color: C.ink + "55" }}>Ages:</span> {b.Ages}</span>}
                      {b.PhotoConsent && <span style={{ fontFamily: body, fontSize: 12, color: C.ink + "88" }}><span style={{ color: C.ink + "55" }}>Photo consent:</span> {b.PhotoConsent}</span>}
                      <span style={{ fontFamily: body, fontSize: 12, color: C.ink + "66" }}><span style={{ color: C.ink + "55" }}>Booked:</span> {b.Timestamp ? new Date(b.Timestamp).toLocaleDateString() : "—"}</span>
                    </div>

                    {/* Per-session rows */}
                    <div style={{ fontFamily: heading, fontWeight: 700, fontSize: 11, color: C.ink + "55", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                      Sessions
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {sessions.map((session, si) => {
                        const key = `${i}-${session}`;
                        const rs = resendState[key];
                        return (
                          <div key={si} style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            background: C.cream, borderRadius: 10, padding: "10px 14px",
                            gap: 12,
                          }}>
                            <span style={{ fontFamily: body, fontWeight: 600, fontSize: 13, color: C.ink }}>{session}</span>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              {!hasEmail && (
                                <span style={{ fontFamily: body, fontSize: 11, color: C.ink + "44", fontStyle: "italic" }}>No email on record</span>
                              )}
                              {hasEmail && rs === "sent" && (
                                <span style={{ fontFamily: body, fontSize: 11, fontWeight: 700, color: C.mint }}>Sent!</span>
                              )}
                              {hasEmail && rs === "error" && (
                                <span style={{ fontFamily: body, fontSize: 11, fontWeight: 700, color: "#e74c3c" }}>Failed — try again</span>
                              )}
                              {hasEmail && (
                                <button
                                  onClick={() => handleResend(b, i, session)}
                                  disabled={rs === "sending"}
                                  style={{
                                    background: rs === "sending" ? C.ink + "11" : C.lilacSoft,
                                    color: rs === "sending" ? C.ink + "44" : C.lilac,
                                    border: `1.5px solid ${C.lilac}44`,
                                    padding: "5px 14px", borderRadius: 50,
                                    fontFamily: heading, fontWeight: 700, fontSize: 11,
                                    cursor: rs === "sending" ? "default" : "pointer",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {rs === "sending" ? "Sending..." : rs === "sent" ? "Resend again" : "Resend email"}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ═══ PARTY ENQUIRIES ═══ */
const PartyEnquiries = ({ data, onUpdate }) => {
  const parties = (data.parties || []).slice().reverse();
  return (
    <div>
      <h2 style={{ fontFamily: heading, fontSize: 22, fontWeight: 800, color: C.ink, marginBottom: 20 }}>Party Enquiries ({parties.length})</h2>
      {parties.length === 0 && <div style={{ fontFamily: body, fontSize: 14, color: C.ink + "77" }}>No party enquiries yet</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {parties.map((e, i) => (
          <div key={i} style={{ background: "#fff", borderRadius: 20, border: `2px solid ${e.Status === "New" ? C.pink + "44" : C.ink + "10"}`, padding: "20px 24px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
              <div>
                <div style={{ fontFamily: heading, fontWeight: 700, fontSize: 16, color: C.ink }}>
                  {e.ChildName}'s {e.Package} party
                </div>
                <div style={{ fontFamily: body, fontSize: 12, color: C.ink + "77" }}>from {e.Name} · {e.Timestamp ? new Date(e.Timestamp).toLocaleDateString() : ""}</div>
              </div>
              <span style={{
                background: e.Status === "New" ? C.pinkSoft : C.mintSoft,
                color: e.Status === "New" ? C.pink : C.mint,
                padding: "4px 14px", borderRadius: 12, fontSize: 11, fontFamily: heading, fontWeight: 700,
              }}>{e.Status}</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 20px", fontFamily: body, fontSize: 13, color: C.ink + "88" }}>
              <div><span style={{ color: C.ink + "55" }}>Date:</span> {e.Date} at {e.Time}</div>
              <div><span style={{ color: C.ink + "55" }}>Age:</span> {e.ChildAge}</div>
              <div><span style={{ color: C.ink + "55" }}>Venue:</span> {e.Venue}</div>
              <div><span style={{ color: C.ink + "55" }}>Theme:</span> {e.Theme || "Not specified"}</div>
              <div><span style={{ color: C.ink + "55" }}>Email:</span> {e.Email}</div>
              <div><span style={{ color: C.ink + "55" }}>Phone:</span> {e.Phone}</div>
              {e.Notes && <div style={{ gridColumn: "span 2" }}><span style={{ color: C.ink + "55" }}>Notes:</span> {e.Notes}</div>}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              {e.Status === "New" && (
                <button onClick={() => onUpdate("Party Enquiries", e._row, "M", "Contacted")} style={{
                  background: C.mint, color: "#fff", border: "none", padding: "8px 18px", borderRadius: 50,
                  fontFamily: heading, fontWeight: 700, fontSize: 12, cursor: "pointer",
                }}>Mark as Contacted</button>
              )}
              <a href={`mailto:${e.Email}?subject=The Funky Bunch - Party Enquiry`} style={{
                background: "transparent", color: C.ink, border: `1.5px solid ${C.ink}20`, padding: "8px 18px", borderRadius: 50,
                fontFamily: heading, fontWeight: 700, fontSize: 12, cursor: "pointer", textDecoration: "none",
              }}>Reply by Email</a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ═══ SCHOOL ENQUIRIES ═══ */
const SchoolEnquiries = ({ data, onUpdate }) => {
  const schools = (data.schools || []).slice().reverse();
  return (
    <div>
      <h2 style={{ fontFamily: heading, fontSize: 22, fontWeight: 800, color: C.ink, marginBottom: 20 }}>School Enquiries ({schools.length})</h2>
      {schools.length === 0 && <div style={{ fontFamily: body, fontSize: 14, color: C.ink + "77" }}>No school enquiries yet</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {schools.map((e, i) => (
          <div key={i} style={{ background: "#fff", borderRadius: 20, border: `2px solid ${e.Status === "New" ? C.blue + "44" : C.ink + "10"}`, padding: "20px 24px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
              <div>
                <div style={{ fontFamily: heading, fontWeight: 700, fontSize: 16, color: C.ink }}>
                  {e.SchoolName}
                </div>
                <div style={{ fontFamily: body, fontSize: 12, color: C.ink + "77" }}>from {e.ContactName} · {e.Timestamp ? new Date(e.Timestamp).toLocaleDateString() : ""}</div>
              </div>
              <span style={{
                background: e.Status === "New" ? C.blueSoft : C.mintSoft,
                color: e.Status === "New" ? C.blue : C.mint,
                padding: "4px 14px", borderRadius: 12, fontSize: 11, fontFamily: heading, fontWeight: 700,
              }}>{e.Status}</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 20px", fontFamily: body, fontSize: 13, color: C.ink + "88" }}>
              <div><span style={{ color: C.ink + "55" }}>Contact:</span> {e.ContactName} {e.Role ? `(${e.Role})` : ""}</div>
              <div><span style={{ color: C.ink + "55" }}>Email:</span> {e.Email}</div>
              <div><span style={{ color: C.ink + "55" }}>Phone:</span> {e.Phone || "Not provided"}</div>
              <div><span style={{ color: C.ink + "55" }}>Location:</span> {e.Location || "Not specified"}</div>
              <div><span style={{ color: C.ink + "55" }}>Session type:</span> {e.SessionInterest}</div>
              <div><span style={{ color: C.ink + "55" }}>Year groups:</span> {e.YearGroups || "Not specified"}</div>
              {e.ApproxChildren && <div><span style={{ color: C.ink + "55" }}>Children:</span> {e.ApproxChildren}</div>}
              {e.PreferredDays && <div><span style={{ color: C.ink + "55" }}>Preferred days:</span> {e.PreferredDays}</div>}
              {e.Notes && <div style={{ gridColumn: "span 2" }}><span style={{ color: C.ink + "55" }}>Notes:</span> {e.Notes}</div>}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              {e.Status === "New" && (
                <button onClick={() => onUpdate("School Enquiries", e._row, "L", "Contacted")} style={{
                  background: C.blue, color: "#fff", border: "none", padding: "8px 18px", borderRadius: 50,
                  fontFamily: heading, fontWeight: 700, fontSize: 12, cursor: "pointer",
                }}>Mark as Contacted</button>
              )}
              <a href={`mailto:${e.Email}?subject=The Funky Bunch - School Workshop Enquiry`} style={{
                background: "transparent", color: C.ink, border: `1.5px solid ${C.ink}20`, padding: "8px 18px", borderRadius: 50,
                fontFamily: heading, fontWeight: 700, fontSize: 12, cursor: "pointer", textDecoration: "none",
              }}>Reply by Email</a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ═══ MONDAY INTEREST ═══ */
const MondayInterest = ({ data, onUpdate }) => {
  const entries = (data.mondayInterest || []).slice().reverse();
  return (
    <div>
      <h2 style={{ fontFamily: heading, fontSize: 22, fontWeight: 800, color: C.ink, marginBottom: 8 }}>Monday Interest ({entries.length})</h2>
      <p style={{ fontFamily: body, fontSize: 13, color: C.ink + "77", marginBottom: 20 }}>
        People who have registered interest in Monday morning classes. Use this list to gauge demand and reach out when bookings open.
      </p>
      {entries.length === 0 && <div style={{ fontFamily: body, fontSize: 14, color: C.ink + "77" }}>No expressions of interest yet</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {entries.map((e, i) => (
          <div key={i} style={{ background: "#fff", borderRadius: 16, border: `2px solid ${e.Status === "New" ? C.lilac + "55" : C.ink + "10"}`, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ fontFamily: heading, fontWeight: 700, fontSize: 15, color: C.ink }}>{e.ParentName}</div>
              <div style={{ fontFamily: body, fontSize: 12, color: C.ink + "77", marginTop: 2 }}>Child: {e.ChildName} · {e.Email}</div>
              <div style={{ fontFamily: body, fontSize: 11, color: C.ink + "55", marginTop: 2 }}>{e.Term} · {e.Timestamp ? new Date(e.Timestamp).toLocaleDateString() : ""}</div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {e.Status === "New" && (
                <button onClick={() => onUpdate("Monday Interest", e._row, "F", "Contacted")} style={{ background: C.lilac, color: "#fff", border: "none", padding: "7px 16px", borderRadius: 50, fontFamily: heading, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>Mark Contacted</button>
              )}
              <a href={`mailto:${e.Email}?subject=The Funky Bunch Monday Classes`} style={{ background: "transparent", color: C.ink, border: `1.5px solid ${C.ink}20`, padding: "7px 16px", borderRadius: 50, fontFamily: heading, fontWeight: 700, fontSize: 12, cursor: "pointer", textDecoration: "none" }}>Email</a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ═══ MESSAGES ═══ */
const Messages = ({ data, onUpdate }) => {
  const messages = (data.messages || []).slice().reverse();
  return (
    <div>
      <h2 style={{ fontFamily: heading, fontSize: 22, fontWeight: 800, color: C.ink, marginBottom: 20 }}>Messages ({messages.length})</h2>
      {messages.length === 0 && <div style={{ fontFamily: body, fontSize: 14, color: C.ink + "77" }}>No messages yet</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ background: "#fff", borderRadius: 16, border: `2px solid ${m.Status === "Unread" ? C.lilac + "44" : C.ink + "08"}`, padding: "18px 20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {m.Status === "Unread" && <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.lilac }} />}
                <span style={{ fontFamily: heading, fontWeight: 700, fontSize: 14, color: C.ink }}>{m.Name}</span>
                <span style={{ fontFamily: body, fontSize: 12, color: C.ink + "55" }}>{m.Email}</span>
              </div>
              <span style={{ fontFamily: body, fontSize: 11, color: C.ink + "55" }}>{m.Timestamp ? new Date(m.Timestamp).toLocaleDateString() : ""}</span>
            </div>
            <p style={{ fontFamily: body, fontSize: 13, color: C.ink + "99", lineHeight: 1.6, margin: 0 }}>{m.Message}</p>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              {m.Status === "Unread" && (
                <button onClick={() => onUpdate("Messages", m._row, "E", "Read")} style={{
                  background: C.lilac + "33", color: C.lilac, border: "none", padding: "6px 14px", borderRadius: 50,
                  fontFamily: heading, fontWeight: 700, fontSize: 11, cursor: "pointer",
                }}>Mark as Read</button>
              )}
              <a href={`mailto:${m.Email}?subject=Re: Your message to The Funky Bunch`} style={{
                background: "transparent", color: C.ink, border: `1.5px solid ${C.ink}20`, padding: "6px 14px", borderRadius: 50,
                fontFamily: heading, fontWeight: 700, fontSize: 11, cursor: "pointer", textDecoration: "none",
              }}>Reply by Email</a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


/* ═══ REFERRALS ═══ */
const Referrals = ({ data, onUpdate }) => {
  const referrals = (data.referrals || []).slice().reverse();
  const discounts = data.discounts || [];
  
  // Count active referral codes
  const referralCodes = discounts.filter(d => d.Code && d.Code.toUpperCase().startsWith("REFER-") && d.Status !== "Expired");
  const rewardCodes = discounts.filter(d => d.Code && d.Code.toUpperCase().startsWith("REWARD-"));
  
  return (
    <div>
      <h2 style={{ fontFamily: heading, fontSize: 22, fontWeight: 800, color: C.ink, marginBottom: 20 }}>Referrals</h2>
      
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 28 }}>
        <Stat label="Active Referral Codes" value={referralCodes.length} color={C.pink} />
        <Stat label="Total Referrals" value={referrals.length} color={C.mint} />
        <Stat label="Pending Rewards" value={referrals.filter(r => r.Status === "Pending").length} color={C.yellow} sub="Need to send" />
        <Stat label="Reward Codes Generated" value={rewardCodes.length} color={C.lilac} />
      </div>

      <div style={{ fontFamily: heading, fontSize: 16, fontWeight: 700, color: C.ink, marginBottom: 12 }}>Pending Rewards</div>
      <div style={{ fontFamily: body, fontSize: 12, color: C.ink + "77", marginBottom: 16 }}>When a referral results in a booking, send the referrer their reward code below.</div>
      
      {referrals.filter(r => r.Status === "Pending").length === 0 && (
        <div style={{ fontFamily: body, fontSize: 13, color: C.ink + "77", marginBottom: 20 }}>No pending rewards</div>
      )}
      
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
        {referrals.filter(r => r.Status === "Pending").map((r, i) => (
          <div key={i} style={{ background: "#fff", borderRadius: 16, border: "2px solid " + C.yellow + "44", padding: "16px 20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div>
                <span style={{ fontFamily: heading, fontWeight: 700, fontSize: 14, color: C.ink }}>{r.ReferrerCode}</span>
                <span style={{ fontFamily: body, fontSize: 12, color: C.ink + "55", marginLeft: 8 }}>referred {r.NewCustomerEmail || "unknown"}</span>
              </div>
              <span style={{ fontFamily: body, fontSize: 11, color: C.ink + "55" }}>{r.Timestamp ? new Date(r.Timestamp).toLocaleDateString() : ""}</span>
            </div>
            <div style={{ fontFamily: body, fontSize: 13, color: C.ink + "88", marginBottom: 10 }}>
              Reward code to send: <span style={{ fontFamily: heading, fontWeight: 700, color: C.pink, background: C.pinkSoft, padding: "2px 10px", borderRadius: 8 }}>{r.RewardCode}</span>
              <span style={{ fontSize: 11, color: C.ink + "55", marginLeft: 6 }}>(£5 off next booking)</span>
            </div>
            <button onClick={() => onUpdate("Referrals", r._row, "E", "Sent")} style={{
              background: C.mint, color: "#fff", border: "none", padding: "8px 18px", borderRadius: 50,
              fontFamily: heading, fontWeight: 700, fontSize: 12, cursor: "pointer",
            }}>Mark as Sent</button>
          </div>
        ))}
      </div>

      <div style={{ fontFamily: heading, fontSize: 16, fontWeight: 700, color: C.ink, marginBottom: 12 }}>All Referral Codes</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {referralCodes.map((d, i) => {
          const used = parseInt(d.TimesUsed || "0");
          const limit = parseInt(d.UsageLimit || "5");
          return (
            <div key={i} style={{ background: "#fff", borderRadius: 12, padding: "12px 16px", border: "1.5px solid " + C.ink + "10", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <span style={{ fontFamily: heading, fontWeight: 700, fontSize: 13, color: C.pink }}>{d.Code}</span>
                <span style={{ fontFamily: body, fontSize: 11, color: C.ink + "55", marginLeft: 8 }}>{d.Description}</span>
              </div>
              <div style={{ fontFamily: body, fontSize: 12, fontWeight: 700, color: used >= limit ? "#e74c3c" : C.mint }}>
                {used}/{limit} used
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ═══ MANAGE CLASSES ═══ */
const ManageClasses = ({ data }) => {
  const bookings = data.bookings || [];
  const MAX = 15;

  const terms = [
    { name: "Term 1", weeks: 3, dates: "19 Mar - 2 Apr", sessions: ["Thu 19 Mar", "Thu 26 Mar", "Thu 2 Apr"] },
    { name: "Easter Holiday Special", weeks: 1, dates: "10 Apr (AM)", sessions: ["Fri 10 Apr"], special: true },
    { name: "Easter Holiday Special (PM)", weeks: 1, dates: "10 Apr (PM)", sessions: ["Fri 10 Apr PM"], special: true },
    { name: "Term 2", weeks: 5, dates: "23 Apr - 21 May", sessions: ["Thu 23 Apr", "Thu 30 Apr", "Thu 7 May", "Thu 14 May", "Thu 21 May"] },
    { name: "Term 3", weeks: 7, dates: "4 Jun - 16 Jul", sessions: ["Thu 4 Jun", "Thu 11 Jun", "Thu 18 Jun", "Thu 25 Jun", "Thu 2 Jul", "Thu 9 Jul", "Thu 16 Jul"] },
    { name: "Term 4", weeks: 8, dates: "3 Sep - 22 Oct", sessions: ["Thu 3 Sep", "Thu 10 Sep", "Thu 17 Sep", "Thu 24 Sep", "Thu 1 Oct", "Thu 8 Oct", "Thu 15 Oct", "Thu 22 Oct"] },
    { name: "Term 5", weeks: 7, dates: "5 Nov - 17 Dec", sessions: ["Thu 5 Nov", "Thu 12 Nov", "Thu 19 Nov", "Thu 26 Nov", "Thu 3 Dec", "Thu 10 Dec", "Thu 17 Dec"] },
  ];

  // Count bookings per session
  const counts = {};
  bookings.forEach(b => {
    const sessions = (b.Sessions || "").split(", ");
    const num = parseInt(b.NumChildren) || 1;
    sessions.forEach(s => { if (s) counts[s] = (counts[s] || 0) + num; });
  });

  return (
    <div>
      <h2 style={{ fontFamily: heading, fontSize: 22, fontWeight: 800, color: C.ink, marginBottom: 20 }}>Manage Classes</h2>
      <div style={{ background: "#fff", borderRadius: 20, border: `2px solid ${C.mint}20`, padding: "24px", marginBottom: 20 }}>
        <div style={{ fontFamily: heading, fontWeight: 700, fontSize: 16, color: C.ink, marginBottom: 4 }}>Class settings</div>
        <div style={{ fontFamily: body, fontSize: 13, color: C.ink + "77", marginBottom: 16 }}>These apply to all sessions</div>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          {[["Day", "Thursday"], ["Time", "10:30 - 11:15"], ["Max capacity", "15 children"], ["Price", "£7.00"]].map(([l, v]) => (
            <div key={l} style={{ flex: "1 1 150px" }}>
              <div style={{ fontFamily: body, fontSize: 12, color: C.ink + "77", marginBottom: 4 }}>{l}</div>
              <div style={{ padding: "10px 14px", borderRadius: 12, background: C.cream, border: `1.5px solid ${C.mint}30`, fontFamily: body, fontSize: 14, color: C.ink }}>{v}</div>
            </div>
          ))}
        </div>
      </div>

      {terms.map((t, ti) => (
        <div key={ti} style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <div style={{ fontFamily: heading, fontWeight: 700, fontSize: 16, color: C.ink }}>
              {t.name}
            </div>
            <span style={{ fontFamily: body, fontWeight: 400, fontSize: 13, color: C.ink + "77" }}>
              {t.weeks === 1 ? "1 session" : `${t.weeks} weeks`} · {t.dates}
            </span>
            {t.special && (
              <span style={{
                fontFamily: body, fontWeight: 600, fontSize: 11,
                color: C.pink, background: C.pinkSoft,
                padding: "2px 10px", borderRadius: 20,
              }}>Holiday Special</span>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {t.sessions.map((s, si) => {
              const booked = counts[s] || 0;
              const spotsLeft = MAX - booked;
              return (
                <div key={si} style={{
                  background: "#fff", borderRadius: 12, padding: "10px 14px", border: `1.5px solid ${booked >= MAX ? "#e74c3c33" : booked > 0 ? C.mint + "33" : C.ink + "10"}`, minWidth: 130,
                }}>
                  <div style={{ fontFamily: heading, fontWeight: 700, fontSize: 13, color: C.ink }}>{s}</div>
                  <div style={{ fontFamily: body, fontSize: 12, color: booked >= MAX ? "#e74c3c" : booked > 10 ? C.yellow : C.mint, fontWeight: 600, marginTop: 2 }}>
                    {booked}/{MAX} booked
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

/* ═══ MAIN DASHBOARD ═══ */
export default function AdminDashboard() {
  const [active, setActive] = useState("overview");
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [data, setData] = useState({ bookings: [], messages: [], parties: [], discounts: [] });
  const [loading, setLoading] = useState(true);

  const ADMIN_PASSWORD = "FunkyBunch2026!";

  const handleLogin = () => {
    if (password === ADMIN_PASSWORD) {
      setAuthenticated(true);
      setError("");
      fetchData();
    } else {
      setError("Incorrect password");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch("/.netlify/functions/admin-data");
      const json = await res.json();
      setData(json);
    } catch (e) {
      console.error("Failed to load data:", e);
    }
    setLoading(false);
  };

  const handleUpdate = async (sheet, row, column, value) => {
    try {
      await fetch("/.netlify/functions/admin-update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sheet, row, column, value }),
      });
      fetchData(); // Refresh
    } catch (e) {
      console.error("Update failed:", e);
    }
  };

  if (!authenticated) {
    return (
      <div style={{ background: "#F5F3EE", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: body }}>
        <link href="https://fonts.googleapis.com/css2?family=Baloo+2:wght@400;500;600;700;800&family=Nunito:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
        <div style={{ maxWidth: 360, width: "100%", textAlign: "center", padding: 24 }}>
          <img src={IMG.logo} alt="The Funky Bunch" style={{ width: 140, margin: "0 auto 20px", display: "block" }} />
          <div style={{ fontFamily: heading, fontSize: 20, fontWeight: 800, color: C.ink, marginBottom: 4 }}>Admin Dashboard</div>
          <div style={{ fontFamily: body, fontSize: 13, color: C.ink + "77", marginBottom: 24 }}>Enter the password to continue</div>
          <input type="password" placeholder="Password" value={password}
            onChange={e => { setPassword(e.target.value); setError(""); }}
            onKeyDown={e => e.key === "Enter" && handleLogin()}
            style={{ width: "100%", padding: "12px 16px", borderRadius: 12, border: error ? "2px solid #e74c3c" : `2px solid ${C.pink}30`, fontSize: 15, outline: "none", background: C.cream, fontFamily: body, marginBottom: 8, textAlign: "center" }} />
          {error && <div style={{ fontFamily: body, fontSize: 12, color: "#e74c3c", marginBottom: 8 }}>{error}</div>}
          <button onClick={handleLogin} style={{ width: "100%", background: C.pink, color: "#fff", border: "none", padding: "12px", borderRadius: 50, fontFamily: heading, fontWeight: 700, fontSize: 15, cursor: "pointer", marginTop: 4 }}>Log In</button>
        </div>
      </div>
    );
  }

  const unreadMessages = (data.messages || []).filter(m => m.Status === "Unread").length;
  const newParties = (data.parties || []).filter(p => p.Status === "New").length;

  const renderContent = () => {
    if (loading) return <div style={{ fontFamily: body, fontSize: 14, color: C.ink + "77", padding: 40, textAlign: "center" }}>Loading...</div>;
    switch (active) {
      case "overview": return <Overview data={data} />;
      case "bookings": return <Bookings data={data} />;
      case "parties": return <PartyEnquiries data={data} onUpdate={handleUpdate} />;
      case "schools": return <SchoolEnquiries data={data} onUpdate={handleUpdate} />;
      case "monday": return <MondayInterest data={data} onUpdate={handleUpdate} />;
      case "messages": return <Messages data={data} onUpdate={handleUpdate} />;
      case "referrals": return <Referrals data={data} onUpdate={handleUpdate} />;
      case "classes": return <ManageClasses data={data} />;
      default: return <Overview data={data} />;
    }
  };

  return (
    <div style={{ background: "#F5F3EE", minHeight: "100vh", fontFamily: body }}>
      <link href="https://fonts.googleapis.com/css2?family=Baloo+2:wght@400;500;600;700;800&family=Nunito:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <style>{`* { margin: 0; padding: 0; box-sizing: border-box; } button:hover { opacity: 0.9; transition: opacity 0.2s; }`}</style>
      <div style={{ background: C.cream, padding: "12px 24px", borderBottom: `2px solid ${C.pink}15`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img src={IMG.logo} alt="The Funky Bunch" style={{ height: 36, objectFit: "contain" }} />
          <span style={{ fontFamily: heading, fontWeight: 700, fontSize: 14, color: C.ink + "88", background: C.pinkSoft, padding: "3px 12px", borderRadius: 10 }}>Admin</span>
        </div>
        <button onClick={fetchData} style={{ fontFamily: body, fontSize: 12, color: C.ink + "77", background: "transparent", border: `1.5px solid ${C.ink}15`, padding: "6px 14px", borderRadius: 50, cursor: "pointer" }}>Refresh</button>
      </div>

      <div style={{ display: "flex", minHeight: "calc(100vh - 60px)" }}>
        <div style={{ width: 200, background: C.cream, padding: "20px 12px", borderRight: `2px solid ${C.pink}10`, flexShrink: 0 }}>
          {sections.map(s => (
            <div key={s.id} onClick={() => setActive(s.id)} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 12, marginBottom: 4, cursor: "pointer",
              background: active === s.id ? C.pinkSoft : "transparent",
            }}>
              <span style={{ fontSize: 14, opacity: 0.6 }}>{s.icon}</span>
              <span style={{ fontFamily: heading, fontWeight: active === s.id ? 700 : 600, fontSize: 13, color: active === s.id ? C.pink : C.ink + "88" }}>{s.label}</span>
              {s.id === "messages" && unreadMessages > 0 && (
                <div style={{ width: 18, height: 18, borderRadius: "50%", background: C.lilac, color: "#fff", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", marginLeft: "auto" }}>{unreadMessages}</div>
              )}
              {s.id === "referrals" && (data.referrals || []).filter(r => r.Status === "Pending").length > 0 && (
                <div style={{ width: 18, height: 18, borderRadius: "50%", background: C.yellow, color: "#fff", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", marginLeft: "auto" }}>{(data.referrals || []).filter(r => r.Status === "Pending").length}</div>
              )}
              {s.id === "parties" && newParties > 0 && (
                <div style={{ width: 18, height: 18, borderRadius: "50%", background: C.pink, color: "#fff", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", marginLeft: "auto" }}>{newParties}</div>
              )}
            </div>
          ))}
        </div>
        <div style={{ flex: 1, padding: "28px 32px", maxWidth: 900 }}>{renderContent()}</div>
      </div>
    </div>
  );
}
